import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Shield, Loader2, AlertTriangle, CheckCircle, Clock,
  Flame, Zap, FileText, Building, Calendar, PoundSterling, Send,
  ShoppingCart, Bell, Wrench, AlertCircle, Play, MailCheck, MailX,
  RefreshCw, User, ExternalLink, ClipboardList, Home, Eye, Download,
  Settings, ToggleLeft, ToggleRight
} from "lucide-react";
import { API } from "@/App";

const complianceTypes = {
  epc: { 
    name: "EPC", 
    fullName: "Energy Performance Certificate",
    icon: FileText, 
    color: "bg-green-100 text-green-700",
    iconColor: "text-green-600"
  },
  gas_safety: { 
    name: "Gas Safety", 
    fullName: "Gas Safety Certificate",
    icon: Flame, 
    color: "bg-orange-100 text-orange-700",
    iconColor: "text-orange-600"
  },
  gas_safety_with_service: { 
    name: "Gas + Service", 
    fullName: "Gas Safety + Boiler Service",
    icon: Flame, 
    color: "bg-orange-100 text-orange-700",
    iconColor: "text-orange-600"
  },
  boiler_service: { 
    name: "Boiler Service", 
    fullName: "Boiler Service (standalone)",
    icon: Wrench, 
    color: "bg-amber-100 text-amber-700",
    iconColor: "text-amber-600"
  },
  eicr: { 
    name: "EICR", 
    fullName: "Electrical Installation Condition Report",
    icon: Zap, 
    color: "bg-blue-100 text-blue-700",
    iconColor: "text-blue-600"
  }
};

export const CompliancePage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  
  const [loading, setLoading] = useState(true);
  const [expiring, setExpiring] = useState({ epc: [], gas_safety: [], eicr: [] });
  const [summary, setSummary] = useState({ total_expiring: 0, expired: 0, expiring_soon: 0 });
  const [pricing, setPricing] = useState([]);
  const [orders, setOrders] = useState([]);
  const [properties, setProperties] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [sendingReminders, setSendingReminders] = useState(false);
  const [runningAutoAlerts, setRunningAutoAlerts] = useState(false);
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const [alertsLoading, setAlertsLoading] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  
  // Inventory reports state
  const [inventoryReports, setInventoryReports] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryFilter, setInventoryFilter] = useState("all");
  const [inventorySearch, setInventorySearch] = useState("");

  // Order modal
  const [orderModal, setOrderModal] = useState(false);
  const [orderData, setOrderData] = useState({
    property_reference: "",
    compliance_type: "gas_safety",
    include_boiler_service: false,
    scheduled_date: "",
    notes: ""
  });
  const [creating, setCreating] = useState(false);
  
  // Request from Landlord modal
  const [requestModal, setRequestModal] = useState(false);
  const [requestData, setRequestData] = useState({
    property_id: "",
    property_address: "",
    certificate_type: "gas_safety",
    landlord_email: "",
    landlord_name: "",
    notes: ""
  });
  const [requestingCert, setRequestingCert] = useState(false);
  
  // Warranty warning modal
  const [warrantyWarning, setWarrantyWarning] = useState(false);
  const [pendingOrder, setPendingOrder] = useState(null);
  
  // Alert Settings state
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [alertSettings, setAlertSettings] = useState({
    epc: { first_alert: 28, second_alert: 14, final_alert: 7, send_sms: true, enabled: true },
    gas_safety: { first_alert: 28, second_alert: 14, final_alert: 7, send_sms: true, enabled: true },
    eicr: { first_alert: 56, second_alert: 28, final_alert: 14, send_sms: true, enabled: true }
  });
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Expired certificates state
  const [expiredCerts, setExpiredCerts] = useState({ gas_safety: [], epc: [], eicr: [] });
  const [expiredCounts, setExpiredCounts] = useState({ gas_safety: 0, epc: 0, eicr: 0, total: 0 });
  const [sendingExpiredAlerts, setSendingExpiredAlerts] = useState(false);
  
  // Bulk operations state
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [showBulkRenewalModal, setShowBulkRenewalModal] = useState(false);
  const [bulkRenewalType, setBulkRenewalType] = useState("gas_safety");
  const [bulkPaymentMethod, setBulkPaymentMethod] = useState("invoice");
  const [processingBulkOrder, setProcessingBulkOrder] = useState(false);
  
  // Bulk inspection state
  const [showBulkInspectionModal, setShowBulkInspectionModal] = useState(false);
  const [bulkInspectionData, setBulkInspectionData] = useState({
    inspection_type: "routine",
    scheduled_date: "",
    frequency_months: 3,
    alert_days_before: 14,
    notes: ""
  });
  const [processingBulkInspection, setProcessingBulkInspection] = useState(false);

  useEffect(() => {
    fetchData();
    fetchSchedulerStatus();
    fetchAlertSettings();
    fetchExpiredCertificates();
  }, []);

  const fetchSchedulerStatus = async () => {
    try {
      const response = await axios.get(`${API}/compliance/scheduler-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error("Error fetching scheduler status:", error);
    }
  };

  const fetchAlertSettings = async () => {
    try {
      const response = await axios.get(`${API}/compliance-alerts/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.thresholds) {
        setAlertSettings(response.data.thresholds);
      }
    } catch (error) {
      console.error("Error fetching alert settings:", error);
    }
  };

  const saveAlertSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/compliance-alerts/settings`, alertSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Alert settings saved successfully!");
      setShowSettingsModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const fetchExpiredCertificates = async () => {
    try {
      const response = await axios.get(`${API}/compliance-alerts/expired-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExpiredCerts(response.data.expired || { gas_safety: [], epc: [], eicr: [] });
      setExpiredCounts(response.data.counts || { gas_safety: 0, epc: 0, eicr: 0, total: 0 });
    } catch (error) {
      console.error("Error fetching expired certificates:", error);
    }
  };

  const handleSendExpiredAlerts = async () => {
    setSendingExpiredAlerts(true);
    try {
      const response = await axios.post(`${API}/compliance-alerts/send-expired-alerts`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Sent ${response.data.alerts_sent} expired certificate alerts (${response.data.sms_sent} SMS)`);
      fetchExpiredCertificates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send expired alerts");
    } finally {
      setSendingExpiredAlerts(false);
    }
  };

  const handleBulkRenewal = async () => {
    if (selectedProperties.length === 0) {
      toast.error("Please select at least one property");
      return;
    }
    setProcessingBulkOrder(true);
    try {
      const response = await axios.post(`${API}/compliance-alerts/bulk-renewal`, {
        property_ids: selectedProperties,
        compliance_type: bulkRenewalType,
        payment_method: bulkPaymentMethod
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Created ${response.data.orders_created} renewal orders! Total: £${response.data.total_cost.toFixed(2)} (${response.data.discount_applied} discount)`);
      setShowBulkRenewalModal(false);
      setSelectedProperties([]);
      fetchData();
      fetchExpiredCertificates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create bulk orders");
    } finally {
      setProcessingBulkOrder(false);
    }
  };

  const handleBulkInspection = async () => {
    if (selectedProperties.length === 0) {
      toast.error("Please select at least one property");
      return;
    }
    if (!bulkInspectionData.scheduled_date) {
      toast.error("Please select a scheduled date");
      return;
    }
    setProcessingBulkInspection(true);
    try {
      const response = await axios.post(`${API}/compliance-alerts/bulk-inspections`, {
        property_ids: selectedProperties,
        ...bulkInspectionData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Scheduled ${response.data.inspections_created} inspections for ${bulkInspectionData.scheduled_date}`);
      setShowBulkInspectionModal(false);
      setSelectedProperties([]);
      setBulkInspectionData({
        inspection_type: "routine",
        scheduled_date: "",
        frequency_months: 3,
        alert_days_before: 14,
        notes: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to schedule inspections");
    } finally {
      setProcessingBulkInspection(false);
    }
  };

  const togglePropertySelection = (propertyId) => {
    setSelectedProperties(prev => 
      prev.includes(propertyId) 
        ? prev.filter(id => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  const selectAllExpired = (certType) => {
    const ids = expiredCerts[certType]?.map(p => p.property_id) || [];
    setSelectedProperties(ids);
    setBulkRenewalType(certType);
  };

  const fetchInventoryReports = async () => {
    setInventoryLoading(true);
    try {
      const response = await axios.get(`${API}/inventory-reports/completed`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInventoryReports(response.data.reports || []);
    } catch (error) {
      console.error("Error fetching inventory reports:", error);
    } finally {
      setInventoryLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [expiringRes, pricingRes, ordersRes, propsRes] = await Promise.all([
        axios.get(`${API}/compliance/expiring`, { headers }),
        axios.get(`${API}/compliance/pricing`, { headers }),
        axios.get(`${API}/compliance/orders`, { headers }),
        axios.get(`${API}/properties`, { headers })
      ]);
      
      setExpiring(expiringRes.data.expiring || {});
      setSummary(expiringRes.data.summary || {});
      setPricing(pricingRes.data.pricing || []);
      setOrders(ordersRes.data.orders || []);
      setProperties(propsRes.data.properties || []);
    } catch (error) {
      console.error("Error fetching compliance data:", error);
      toast.error("Failed to load compliance data");
    } finally {
      setLoading(false);
    }
  };

  const submitOrder = async (data) => {
    setCreating(true);
    try {
      const response = await axios.post(`${API}/compliance/orders`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Order created - £${response.data.total.toFixed(2)} inc VAT`);
      setOrderModal(false);
      setOrderData({ property_reference: "", compliance_type: "gas_safety", include_boiler_service: false, scheduled_date: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create order");
    } finally {
      setCreating(false);
    }
  };

  const handleCreateOrder = async () => {
    if (!orderData.property_reference || !orderData.compliance_type) {
      toast.error("Please select a property and service type");
      return;
    }

    // If gas safety without boiler service, show warranty warning
    if (orderData.compliance_type === "gas_safety" && !orderData.include_boiler_service) {
      setPendingOrder(orderData);
      setWarrantyWarning(true);
      return;
    }

    // Otherwise proceed directly
    await submitOrder(orderData);
  };

  const handleWarrantyConfirm = async (addService) => {
    setWarrantyWarning(false);
    if (addService) {
      // User wants to add boiler service
      await submitOrder({ ...pendingOrder, include_boiler_service: true });
    } else {
      // User declines - proceed without service
      await submitOrder(pendingOrder);
    }
    setPendingOrder(null);
  };

  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/compliance/send-reminders`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Failed to send reminders");
    } finally {
      setSendingReminders(false);
    }
  };

  const handleRunAutoAlerts = async () => {
    setRunningAutoAlerts(true);
    try {
      const response = await axios.post(`${API}/compliance/run-auto-alerts`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Auto-alerts processed: ${response.data.alerts_sent} alerts sent, ${response.data.auto_booked} auto-booked`);
      fetchComplianceAlerts(); // Refresh the alerts list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to run auto-alerts");
    } finally {
      setRunningAutoAlerts(false);
    }
  };

  const fetchComplianceAlerts = async () => {
    setAlertsLoading(true);
    try {
      const response = await axios.get(`${API}/compliance/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComplianceAlerts(response.data.alerts || []);
    } catch (error) {
      console.error("Error fetching compliance alerts:", error);
    } finally {
      setAlertsLoading(false);
    }
  };

  // Request Certificate from Landlord
  const handleRequestCertificate = async () => {
    if (!requestData.landlord_email || !requestData.certificate_type) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setRequestingCert(true);
    try {
      await axios.post(`${API}/compliance/request-certificate`, requestData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Request sent to ${requestData.landlord_email}`);
      setRequestModal(false);
      setRequestData({
        property_id: "", property_address: "", certificate_type: "gas_safety",
        landlord_email: "", landlord_name: "", notes: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send request");
    } finally {
      setRequestingCert(false);
    }
  };

  // Open request modal from expiring item
  const openRequestFromExpiring = (item) => {
    setRequestData({
      property_id: item.property_id,
      property_address: item.address || item.property_address,
      certificate_type: item.compliance_type || "gas_safety",
      landlord_email: item.landlord_email || "",
      landlord_name: item.landlord_name || "",
      notes: ""
    });
    setRequestModal(true);
  };

  // Fetch alerts when switching to auto_alerts tab
  useEffect(() => {
    if (activeTab === "auto_alerts") {
      fetchComplianceAlerts();
    }
    if (activeTab === "inventory") {
      fetchInventoryReports();
    }
  }, [activeTab]);

  const getAlertStatusBadge = (alert) => {
    if (alert.auto_booked) {
      return <Badge className="bg-green-100 text-green-700">Auto-Booked</Badge>;
    }
    if (alert.booking_declined) {
      return <Badge className="bg-slate-100 text-slate-700">Declined</Badge>;
    }
    if (alert.response_received) {
      return <Badge className="bg-blue-100 text-blue-700">Responded</Badge>;
    }
    // Check if past 14-day window
    const alertDate = new Date(alert.initial_alert_sent);
    const daysSince = Math.floor((new Date() - alertDate) / (1000 * 60 * 60 * 24));
    if (daysSince >= 14) {
      return <Badge className="bg-amber-100 text-amber-700">Pending Auto-Book</Badge>;
    }
    return <Badge className="bg-yellow-100 text-yellow-700">Awaiting Response ({14 - daysSince}d left)</Badge>;
  };

  const openOrderFromExpiring = (item, compType) => {
    setOrderData({
      property_reference: item.property_reference,
      compliance_type: compType,
      scheduled_date: "",
      notes: ""
    });
    setOrderModal(true);
  };

  const getStatusBadge = (days) => {
    if (days < 0) return <Badge className="bg-red-500 text-white">Expired</Badge>;
    if (days <= 7) return <Badge className="bg-red-100 text-red-700">Expires in {days} days</Badge>;
    if (days <= 30) return <Badge className="bg-amber-100 text-amber-700">Expires in {days} days</Badge>;
    return <Badge className="bg-yellow-100 text-yellow-700">Expires in {days} days</Badge>;
  };

  const selectedPricing = pricing.find(p => p.type === orderData.compliance_type);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold font-outfit">Compliance Management</h1>
                <p className="text-sm text-muted-foreground">Gas Safety, EPC & EICR tracking</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {(user.role === "admin" || user.role === "director") && (
                <>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowSettingsModal(true)}
                    className="border-slate-300"
                    data-testid="alert-settings-btn"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Alert Settings
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleRunAutoAlerts}
                    disabled={runningAutoAlerts}
                    className="border-green-300 text-green-700 hover:bg-green-50"
                    data-testid="run-auto-alerts-btn"
                  >
                    {runningAutoAlerts ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Run Auto-Alerts
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleSendReminders}
                    disabled={sendingReminders}
                  >
                    {sendingReminders ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Bell className="w-4 h-4 mr-2" />
                    )}
                    Send Reminders
                  </Button>
                </>
              )}
              <Button onClick={() => setOrderModal(true)} data-testid="new-order-btn">
                <ShoppingCart className="w-4 h-4 mr-2" />
                Book Service
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-700">{summary.expired}</p>
                  <p className="text-sm text-red-600">Expired</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">{summary.expiring_soon}</p>
                  <p className="text-sm text-amber-600">Expiring Soon</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{orders.filter(o => o.status === "pending").length}</p>
                  <p className="text-sm text-muted-foreground">Pending Orders</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{orders.filter(o => o.status === "completed").length}</p>
                  <p className="text-sm text-muted-foreground">Completed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {pricing.filter(s => ["gas_safety", "epc", "eicr"].includes(s.type)).map((service) => {
            const config = complianceTypes[service.type];
            if (!config) return null;
            const Icon = config.icon;
            const priceExVat = service.price_ex_vat || service.price || 0;
            const vatAmount = service.vat_amount || (priceExVat * 0.2);
            const totalIncVat = service.total_inc_vat || (priceExVat * 1.2);
            const vatRate = service.vat_rate || "20%";
            return (
              <Card key={service.type} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-10 h-10 rounded-full ${config.color.split(' ')[0]} flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 ${config.iconColor}`} />
                    </div>
                    <div>
                      <p className="font-semibold">{config.name}</p>
                      <p className="text-xs text-muted-foreground">{config.fullName}</p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Price (ex VAT)</span>
                      <span>£{priceExVat.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>VAT ({vatRate})</span>
                      <span>£{vatAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-semibold pt-1 border-t">
                      <span>Total</span>
                      <span className="text-primary">£{totalIncVat.toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="flex-wrap">
            <TabsTrigger value="overview">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Expiring ({summary.total_expiring})
            </TabsTrigger>
            <TabsTrigger value="expired" className="text-red-600">
              <AlertCircle className="w-4 h-4 mr-2" />
              Expired ({expiredCounts.total})
            </TabsTrigger>
            <TabsTrigger value="gas_safety">
              <Flame className="w-4 h-4 mr-2" />
              Gas Safety ({expiring.gas_safety?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="epc">
              <FileText className="w-4 h-4 mr-2" />
              EPC ({expiring.epc?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="eicr">
              <Zap className="w-4 h-4 mr-2" />
              EICR ({expiring.eicr?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="orders">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Orders ({orders.length})
            </TabsTrigger>
            <TabsTrigger value="auto_alerts" data-testid="tab-auto-alerts">
              <MailCheck className="w-4 h-4 mr-2" />
              Auto Alerts ({complianceAlerts.length})
            </TabsTrigger>
            <TabsTrigger value="inventory" data-testid="tab-inventory" onClick={() => fetchInventoryReports()}>
              <ClipboardList className="w-4 h-4 mr-2" />
              Inventory ({inventoryReports.length})
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {Object.entries(expiring).map(([type, items]) => {
              if (!items.length) return null;
              const config = complianceTypes[type];
              const Icon = config.icon;
              
              return (
                <div key={type}>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Icon className={`w-5 h-5 ${config.iconColor}`} />
                    {config.fullName} ({items.length})
                  </h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {items.slice(0, 6).map((item) => (
                      <Card key={`${type}-${item.property_reference}`} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <Badge variant="outline">#{item.property_reference}</Badge>
                            {getStatusBadge(item.days_until_expiry)}
                          </div>
                          <p className="font-medium text-sm mb-1 line-clamp-2">{item.address}</p>
                          <p className="text-xs text-muted-foreground mb-3">
                            Expires: {item.expiry_date}
                          </p>
                          <Button 
                            size="sm" 
                            className="w-full"
                            onClick={() => openOrderFromExpiring(item, type)}
                          >
                            Book Renewal
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              );
            })}
            
            {summary.total_expiring === 0 && (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-12 h-12 text-green-300 mx-auto mb-4" />
                  <p className="text-muted-foreground">All compliance certificates are up to date!</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* EXPIRED CERTIFICATES TAB */}
          <TabsContent value="expired" className="space-y-6">
            {/* Expired Summary Cards */}
            <div className="grid md:grid-cols-4 gap-4">
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-red-600 font-medium">Total Expired</p>
                      <p className="text-3xl font-bold text-red-700">{expiredCounts.total}</p>
                    </div>
                    <AlertCircle className="w-10 h-10 text-red-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-orange-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Gas Safety</p>
                      <p className="text-2xl font-bold">{expiredCounts.gas_safety}</p>
                    </div>
                    <Flame className="w-8 h-8 text-orange-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">EPC</p>
                      <p className="text-2xl font-bold">{expiredCounts.epc}</p>
                    </div>
                    <FileText className="w-8 h-8 text-green-400" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">EICR</p>
                      <p className="text-2xl font-bold">{expiredCounts.eicr}</p>
                    </div>
                    <Zap className="w-8 h-8 text-blue-400" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <Button 
                variant="destructive" 
                onClick={handleSendExpiredAlerts}
                disabled={sendingExpiredAlerts || expiredCounts.total === 0}
                data-testid="send-expired-alerts-btn"
              >
                {sendingExpiredAlerts ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Send Expired Alerts to Landlords
              </Button>
              {selectedProperties.length > 0 && (
                <>
                  <Button onClick={() => setShowBulkRenewalModal(true)}>
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Bulk Order ({selectedProperties.length} selected)
                  </Button>
                  <Button variant="outline" onClick={() => setShowBulkInspectionModal(true)}>
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule Inspections
                  </Button>
                  <Button variant="ghost" onClick={() => setSelectedProperties([])}>
                    Clear Selection
                  </Button>
                </>
              )}
            </div>

            {/* Expired Certificate Tables */}
            {["gas_safety", "epc", "eicr"].map((certType) => {
              const items = expiredCerts[certType] || [];
              if (items.length === 0) return null;
              
              const config = complianceTypes[certType];
              const Icon = config.icon;
              
              return (
                <Card key={certType}>
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <Icon className={`w-5 h-5 ${config.iconColor}`} />
                        Expired {config.fullName} ({items.length})
                      </CardTitle>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => selectAllExpired(certType)}
                      >
                        Select All
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-md border overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="p-3 text-left w-10">
                              <Checkbox 
                                checked={items.every(i => selectedProperties.includes(i.property_id))}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedProperties(prev => [...new Set([...prev, ...items.map(i => i.property_id)])]);
                                  } else {
                                    setSelectedProperties(prev => prev.filter(id => !items.map(i => i.property_id).includes(id)));
                                  }
                                }}
                              />
                            </th>
                            <th className="p-3 text-left">Property</th>
                            <th className="p-3 text-left">Landlord</th>
                            <th className="p-3 text-left">Expiry Date</th>
                            <th className="p-3 text-left">Days Overdue</th>
                            <th className="p-3 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.slice(0, 20).map((item, idx) => (
                            <tr key={item.property_id} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                              <td className="p-3">
                                <Checkbox 
                                  checked={selectedProperties.includes(item.property_id)}
                                  onCheckedChange={() => togglePropertySelection(item.property_id)}
                                />
                              </td>
                              <td className="p-3">
                                <p className="font-medium">{item.address?.substring(0, 40)}{item.address?.length > 40 ? "..." : ""}</p>
                              </td>
                              <td className="p-3">
                                <p className="text-muted-foreground">{item.landlord_name || "—"}</p>
                                <p className="text-xs text-muted-foreground">{item.landlord_email || ""}</p>
                              </td>
                              <td className="p-3">{item.expiry_date}</td>
                              <td className="p-3">
                                <Badge variant="destructive">{item.days_overdue} days</Badge>
                              </td>
                              <td className="p-3">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    setSelectedProperties([item.property_id]);
                                    setBulkRenewalType(certType);
                                    setShowBulkRenewalModal(true);
                                  }}
                                >
                                  Order
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {items.length > 20 && (
                      <p className="text-sm text-muted-foreground mt-2 text-center">
                        Showing 20 of {items.length} expired certificates
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}

            {expiredCounts.total === 0 && (
              <Card className="border-dashed border-green-300">
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
                  <p className="text-green-700 font-medium">No expired certificates! All compliance is up to date.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Individual Type Tabs */}
          {["gas_safety", "epc", "eicr"].map((type) => {
            const config = complianceTypes[type];
            const Icon = config.icon;
            const items = expiring[type] || [];
            
            return (
              <TabsContent key={type} value={type} className="space-y-4">
                {items.length === 0 ? (
                  <Card className="border-dashed">
                    <CardContent className="py-12 text-center">
                      <Icon className={`w-12 h-12 ${config.iconColor} opacity-30 mx-auto mb-4`} />
                      <p className="text-muted-foreground">No {config.name} certificates expiring soon</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {items.map((item) => (
                      <Card key={item.property_reference}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <Badge variant="outline">#{item.property_reference}</Badge>
                            {getStatusBadge(item.days_until_expiry)}
                          </div>
                          <p className="font-medium mb-1">{item.address}</p>
                          <p className="text-sm text-muted-foreground mb-1">
                            Landlord: {item.landlord_name || "—"}
                          </p>
                          <p className="text-sm text-muted-foreground mb-3">
                            Expires: {item.expiry_date}
                          </p>
                          <div className="flex gap-2">
                            <Button 
                              size="sm" 
                              className="flex-1"
                              onClick={() => openOrderFromExpiring(item, type)}
                            >
                              <ShoppingCart className="w-4 h-4 mr-2" />
                              Book {config.name}
                            </Button>
                            {item.landlord_email && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openRequestFromExpiring({...item, compliance_type: type})}
                                title="Request certificate from landlord"
                                data-testid="request-from-landlord-btn"
                              >
                                <Send className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            );
          })}

          {/* Orders Tab */}
          <TabsContent value="orders" className="space-y-4">
            {orders.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-muted-foreground">No orders yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {orders.map((order) => {
                  const config = complianceTypes[order.compliance_type];
                  const Icon = config?.icon || FileText;
                  
                  return (
                    <Card key={order.order_id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full ${config?.color.split(' ')[0] || 'bg-slate-100'} flex items-center justify-center`}>
                              <Icon className={`w-5 h-5 ${config?.iconColor || 'text-slate-600'}`} />
                            </div>
                            <div>
                              <p className="font-medium">{order.service_name}</p>
                              <p className="text-sm text-muted-foreground">{order.property_address}</p>
                              <p className="text-xs text-muted-foreground">
                                Ordered: {new Date(order.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className={
                              order.status === "completed" ? "bg-green-100 text-green-700" :
                              order.status === "scheduled" ? "bg-blue-100 text-blue-700" :
                              order.status === "cancelled" ? "bg-slate-100 text-slate-700" :
                              "bg-amber-100 text-amber-700"
                            }>
                              {order.status}
                            </Badge>
                            <p className="font-semibold mt-1">£{(order.total_inc_vat || 0).toFixed(2)}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Auto Alerts Tab */}
          <TabsContent value="auto_alerts" className="space-y-4">
            {/* Scheduler Status Banner */}
            {schedulerStatus && (
              <div className={`rounded-lg p-4 flex items-center justify-between ${schedulerStatus.scheduler_running ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${schedulerStatus.scheduler_running ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`} />
                  <div>
                    <p className="font-medium text-sm">
                      {schedulerStatus.scheduler_running ? '🤖 Auto-Alert Scheduler Active' : '⚠️ Scheduler Not Running'}
                    </p>
                    {schedulerStatus.next_run_time && (
                      <p className="text-xs text-muted-foreground">
                        Next run: {new Date(schedulerStatus.next_run_time).toLocaleString('en-GB', { 
                          weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' 
                        })} • {schedulerStatus.trigger}
                      </p>
                    )}
                  </div>
                </div>
                <Badge variant="outline" className={schedulerStatus.scheduler_running ? 'text-green-700 border-green-300' : 'text-amber-700 border-amber-300'}>
                  {schedulerStatus.scheduler_running ? 'Running' : 'Stopped'}
                </Badge>
              </div>
            )}

            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold">Automated Compliance Alerts</h3>
                <p className="text-sm text-muted-foreground">
                  Alerts sent 4 weeks before expiry. Auto-books if no response within 14 days.
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={fetchComplianceAlerts}
                disabled={alertsLoading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${alertsLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {alertsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : complianceAlerts.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <MailCheck className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-medium text-slate-700 mb-1">No compliance alerts sent yet</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {schedulerStatus?.scheduler_running 
                      ? "The scheduler will automatically check for expiring certificates daily at 9:00 AM."
                      : "Click \"Run Auto-Alerts\" to check for expiring certificates and send alerts."
                    }
                  </p>
                  <Button onClick={handleRunAutoAlerts} disabled={runningAutoAlerts}>
                    {runningAutoAlerts ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Run Auto-Alerts Now
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Alert Summary Stats */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-blue-600">{complianceAlerts.length}</p>
                      <p className="text-xs text-muted-foreground">Total Alerts</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-yellow-600">
                        {complianceAlerts.filter(a => !a.auto_booked && !a.booking_declined && !a.response_received).length}
                      </p>
                      <p className="text-xs text-muted-foreground">Awaiting Response</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {complianceAlerts.filter(a => a.auto_booked || a.response_received).length}
                      </p>
                      <p className="text-xs text-muted-foreground">Booked</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-2xl font-bold text-slate-600">
                        {complianceAlerts.filter(a => a.booking_declined).length}
                      </p>
                      <p className="text-xs text-muted-foreground">Arranged Elsewhere</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Alerts List */}
                <div className="space-y-3">
                  {complianceAlerts.map((alert) => {
                    const config = complianceTypes[alert.compliance_type];
                    const Icon = config?.icon || FileText;
                    
                    return (
                      <Card key={alert.alert_id} data-testid={`alert-${alert.alert_id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex items-start gap-4">
                              <div className={`w-10 h-10 rounded-full ${config?.color.split(' ')[0] || 'bg-slate-100'} flex items-center justify-center`}>
                                <Icon className={`w-5 h-5 ${config?.iconColor || 'text-slate-600'}`} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <p className="font-medium">{alert.compliance_name}</p>
                                  {getAlertStatusBadge(alert)}
                                </div>
                                <p className="text-sm text-muted-foreground">{alert.property_address}</p>
                                <p className="text-xs text-muted-foreground">
                                  Ref: #{alert.property_reference}
                                </p>
                                <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <User className="w-3 h-3" />
                                    {alert.landlord_name || alert.landlord_email}
                                  </span>
                                  <span>
                                    Expires: {alert.expiry_date}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right text-xs text-muted-foreground">
                              <p>Alert Sent:</p>
                              <p className="font-medium">
                                {new Date(alert.initial_alert_sent).toLocaleDateString('en-GB', {
                                  day: 'numeric', month: 'short', year: 'numeric'
                                })}
                              </p>
                              {alert.auto_booked && (
                                <div className="mt-2 bg-green-50 px-2 py-1 rounded">
                                  <p className="text-green-700 font-medium">Auto-booked</p>
                                  <p className="text-green-600">
                                    {new Date(alert.auto_booked_at).toLocaleDateString('en-GB', {
                                      day: 'numeric', month: 'short'
                                    })}
                                  </p>
                                </div>
                              )}
                              {alert.booking_declined && (
                                <div className="mt-2 bg-slate-50 px-2 py-1 rounded">
                                  <p className="text-slate-700 font-medium">Declined</p>
                                  <p className="text-slate-600">
                                    {new Date(alert.declined_at).toLocaleDateString('en-GB', {
                                      day: 'numeric', month: 'short'
                                    })}
                                  </p>
                                </div>
                              )}
                              {alert.order_id && (
                                <div className="mt-2">
                                  <Badge variant="outline" className="text-xs">
                                    Order: {alert.order_id}
                                  </Badge>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
          </TabsContent>

          {/* Inventory Tab */}
          <TabsContent value="inventory" data-testid="inventory-tab" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <ClipboardList className="w-5 h-5 text-purple-600" />
                      Inventory Reports
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      All completed inventory reports, sorted oldest to newest
                    </p>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/inventory/reports')}
                    data-testid="view-all-reports-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View All Reports
                  </Button>
                </div>
                {/* Filters */}
                <div className="flex flex-wrap gap-3 mt-4">
                  <Input
                    placeholder="Search by address, tenant..."
                    value={inventorySearch}
                    onChange={(e) => setInventorySearch(e.target.value)}
                    className="max-w-xs"
                    data-testid="inventory-search"
                  />
                  <select
                    value={inventoryFilter}
                    onChange={(e) => setInventoryFilter(e.target.value)}
                    className="border rounded-md px-3 py-2 text-sm"
                    data-testid="inventory-filter"
                  >
                    <option value="all">All Types</option>
                    <option value="inventory">Inventory</option>
                    <option value="check_in">Check-In</option>
                    <option value="check_out">Check-Out</option>
                    <option value="interim_inspection">Interim Inspection</option>
                  </select>
                </div>
              </CardHeader>
              <CardContent>
                {inventoryLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                  </div>
                ) : (() => {
                  // Filter reports
                  const filteredReports = inventoryReports.filter(report => {
                    const matchesSearch = !inventorySearch || 
                      report.property_address?.toLowerCase().includes(inventorySearch.toLowerCase()) ||
                      report.tenant_name?.toLowerCase().includes(inventorySearch.toLowerCase()) ||
                      report.property_postcode?.toLowerCase().includes(inventorySearch.toLowerCase());
                    const matchesType = inventoryFilter === "all" || report.report_type === inventoryFilter;
                    return matchesSearch && matchesType;
                  });
                  
                  return filteredReports.length > 0 ? (
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground mb-2">
                        Showing {filteredReports.length} of {inventoryReports.length} reports
                      </p>
                      {filteredReports.map((report) => (
                        <div 
                          key={report.report_id} 
                          className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200 hover:bg-purple-100 transition-colors cursor-pointer"
                          onClick={() => navigate(`/inventory/report/${report.report_id}`)}
                          data-testid={`inventory-report-${report.report_id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                              <Home className="w-6 h-6 text-purple-600" />
                            </div>
                            <div>
                              <p className="font-medium">{report.property_address}</p>
                              <p className="text-sm text-muted-foreground">
                                {report.report_type_label || report.report_type?.replace('_', ' ')}
                              </p>
                              {report.tenant_name && (
                                <p className="text-sm text-muted-foreground flex items-center gap-1">
                                  <User className="w-3 h-3" /> {report.tenant_name}
                                </p>
                              )}
                              {report.tenancy_reference && (
                                <Badge variant="outline" className="mt-1 text-xs">
                                  Ref: {report.tenancy_reference}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <Badge className="bg-purple-100 text-purple-700">
                              Complete
                            </Badge>
                            <p className="text-xs text-muted-foreground">
                              {report.visit_date && new Date(report.visit_date).toLocaleDateString('en-GB', { 
                                day: 'numeric', 
                                month: 'short', 
                                year: 'numeric' 
                              })}
                            </p>
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/inventory/report/${report.report_id}`);
                                }}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(`${API}/inventory-reports/${report.report_id}/download`, '_blank');
                                }}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                PDF
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <ClipboardList className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                      <p>{inventorySearch || inventoryFilter !== "all" ? "No matching reports found" : "No completed inventory reports"}</p>
                      <p className="text-sm mt-1">{inventorySearch || inventoryFilter !== "all" ? "Try adjusting your filters" : "Completed inventory reports will appear here"}</p>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Order Modal */}
      <Dialog open={orderModal} onOpenChange={setOrderModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Book Compliance Service</DialogTitle>
            <DialogDescription>
              Order a Gas Safety, EPC, or EICR certificate
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Service Type</Label>
              <Select
                value={orderData.compliance_type}
                onValueChange={(v) => setOrderData({ ...orderData, compliance_type: v, include_boiler_service: false })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gas_safety">
                    <span className="flex items-center gap-2">
                      <Flame className="w-4 h-4 text-orange-500" />
                      Gas Safety - £125 + VAT
                    </span>
                  </SelectItem>
                  <SelectItem value="epc">
                    <span className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-green-500" />
                      EPC - £125 + VAT
                    </span>
                  </SelectItem>
                  <SelectItem value="eicr">
                    <span className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-blue-500" />
                      EICR - £250 + VAT
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Boiler Service Upsell - only for Gas Safety */}
            {orderData.compliance_type === "gas_safety" && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="boiler_service"
                    checked={orderData.include_boiler_service}
                    onCheckedChange={(checked) => setOrderData({ ...orderData, include_boiler_service: checked })}
                  />
                  <div className="flex-1">
                    <label htmlFor="boiler_service" className="font-medium text-sm cursor-pointer flex items-center gap-2">
                      <Wrench className="w-4 h-4 text-orange-600" />
                      Add Boiler Service (+£50 + VAT)
                    </label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Recommended: Annual boiler servicing keeps your boiler running efficiently and maintains warranty compliance.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Property</Label>
              <Select
                value={orderData.property_reference}
                onValueChange={(v) => setOrderData({ ...orderData, property_reference: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (
                    <SelectItem key={p.reference} value={p.reference}>
                      {p.reference} - {p.display_address}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Preferred Date (optional)</Label>
              <Input
                type="date"
                value={orderData.scheduled_date}
                onChange={(e) => setOrderData({ ...orderData, scheduled_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="Any special instructions or access details..."
                value={orderData.notes}
                onChange={(e) => setOrderData({ ...orderData, notes: e.target.value })}
              />
            </div>

            {/* Dynamic Pricing */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              {orderData.compliance_type === "gas_safety" ? (
                <>
                  <div className="flex justify-between text-sm">
                    <span>Gas Safety Certificate</span>
                    <span>£125.00</span>
                  </div>
                  {orderData.include_boiler_service && (
                    <div className="flex justify-between text-sm text-orange-600">
                      <span>+ Boiler Service</span>
                      <span>£50.00</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>VAT (20%)</span>
                    <span>£{orderData.include_boiler_service ? "35.00" : "25.00"}</span>
                  </div>
                  <div className="flex justify-between font-semibold pt-2 border-t">
                    <span>Total</span>
                    <span className="text-lg text-primary">
                      £{orderData.include_boiler_service ? "210.00" : "150.00"}
                    </span>
                  </div>
                </>
              ) : selectedPricing && (
                <>
                  <div className="flex justify-between text-sm">
                    <span>Price (ex VAT)</span>
                    <span>£{(selectedPricing.price_ex_vat || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>VAT (20%)</span>
                    <span>£{(selectedPricing.vat_amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-semibold pt-2 border-t">
                    <span>Total</span>
                    <span className="text-lg text-primary">£{(selectedPricing.total_inc_vat || 0).toFixed(2)}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderModal(false)}>Cancel</Button>
            <Button onClick={handleCreateOrder} disabled={creating}>
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Place Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Warranty Warning Dialog */}
      <AlertDialog open={warrantyWarning} onOpenChange={setWarrantyWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" />
              Boiler Service Recommended
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                You&apos;ve chosen not to include a boiler service with your Gas Safety Certificate.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
                <p className="font-medium mb-2">Important Notice:</p>
                <p className="text-sm">
                  Most boiler manufacturers require an <strong>annual service</strong> to maintain the warranty. 
                  Without this, your warranty could be <strong>invalidated</strong>, leaving you liable for 
                  potentially expensive repair costs.
                </p>
              </div>
              <p className="font-medium">
                Add boiler service for just <span className="text-primary">£50 + VAT</span> (total £210 inc VAT)?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => handleWarrantyConfirm(false)}>
              No thanks, just Gas Safety
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => handleWarrantyConfirm(true)} className="bg-orange-600 hover:bg-orange-700">
              <Wrench className="w-4 h-4 mr-2" />
              Yes, add Boiler Service
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Request Certificate from Landlord Modal */}
      <Dialog open={requestModal} onOpenChange={setRequestModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-600" />
              Request Certificate from Landlord
            </DialogTitle>
            <DialogDescription>
              Send an email to the landlord asking them to provide the certificate
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Property</Label>
              <Input value={requestData.property_address} disabled className="bg-slate-50" />
            </div>
            
            <div>
              <Label>Certificate Type</Label>
              <Select 
                value={requestData.certificate_type} 
                onValueChange={(v) => setRequestData({...requestData, certificate_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gas_safety">Gas Safety Certificate</SelectItem>
                  <SelectItem value="epc">EPC</SelectItem>
                  <SelectItem value="eicr">EICR</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Landlord Name</Label>
              <Input 
                value={requestData.landlord_name} 
                onChange={(e) => setRequestData({...requestData, landlord_name: e.target.value})}
                placeholder="Landlord name"
              />
            </div>
            
            <div>
              <Label>Landlord Email *</Label>
              <Input 
                type="email"
                value={requestData.landlord_email} 
                onChange={(e) => setRequestData({...requestData, landlord_email: e.target.value})}
                placeholder="landlord@email.com"
              />
            </div>
            
            <div>
              <Label>Additional Notes (Optional)</Label>
              <Textarea 
                value={requestData.notes} 
                onChange={(e) => setRequestData({...requestData, notes: e.target.value})}
                placeholder="Any specific message for the landlord..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestModal(false)}>Cancel</Button>
            <Button onClick={handleRequestCertificate} disabled={requestingCert || !requestData.landlord_email}>
              {requestingCert ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Compliance Alert Settings
            </DialogTitle>
            <DialogDescription>
              Configure when alerts are sent for each certificate type. Alerts are sent at each threshold if not already actioned.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Gas Safety Settings */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Flame className="w-5 h-5 text-orange-600" />
                  <h3 className="font-semibold">Gas Safety Certificate</h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAlertSettings({
                    ...alertSettings,
                    gas_safety: { ...alertSettings.gas_safety, enabled: !alertSettings.gas_safety.enabled }
                  })}
                  className={alertSettings.gas_safety.enabled ? "text-green-600" : "text-slate-400"}
                >
                  {alertSettings.gas_safety.enabled ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                  <span className="ml-1 text-xs">{alertSettings.gas_safety.enabled ? "Enabled" : "Disabled"}</span>
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">First Alert</Label>
                  <Select 
                    value={String(alertSettings.gas_safety.first_alert)}
                    onValueChange={(v) => setAlertSettings({
                      ...alertSettings,
                      gas_safety: { ...alertSettings.gas_safety, first_alert: parseInt(v) }
                    })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="56">56 days</SelectItem>
                      <SelectItem value="42">42 days</SelectItem>
                      <SelectItem value="28">28 days</SelectItem>
                      <SelectItem value="21">21 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Second Alert</Label>
                  <Select 
                    value={String(alertSettings.gas_safety.second_alert)}
                    onValueChange={(v) => setAlertSettings({
                      ...alertSettings,
                      gas_safety: { ...alertSettings.gas_safety, second_alert: parseInt(v) }
                    })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="21">21 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="10">10 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Final Alert</Label>
                  <Select 
                    value={String(alertSettings.gas_safety.final_alert)}
                    onValueChange={(v) => setAlertSettings({
                      ...alertSettings,
                      gas_safety: { ...alertSettings.gas_safety, final_alert: parseInt(v) }
                    })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="3">3 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Checkbox 
                  checked={alertSettings.gas_safety.send_sms}
                  onCheckedChange={(checked) => setAlertSettings({
                    ...alertSettings,
                    gas_safety: { ...alertSettings.gas_safety, send_sms: checked }
                  })}
                />
                <Label className="text-sm">Send SMS alerts</Label>
              </div>
            </div>

            {/* EPC Settings */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-green-600" />
                  <h3 className="font-semibold">EPC (Energy Performance Certificate)</h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAlertSettings({
                    ...alertSettings,
                    epc: { ...alertSettings.epc, enabled: !alertSettings.epc.enabled }
                  })}
                  className={alertSettings.epc.enabled ? "text-green-600" : "text-slate-400"}
                >
                  {alertSettings.epc.enabled ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                  <span className="ml-1 text-xs">{alertSettings.epc.enabled ? "Enabled" : "Disabled"}</span>
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">First Alert</Label>
                  <Select 
                    value={String(alertSettings.epc.first_alert)}
                    onValueChange={(v) => setAlertSettings({
                      ...alertSettings,
                      epc: { ...alertSettings.epc, first_alert: parseInt(v) }
                    })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="56">56 days</SelectItem>
                      <SelectItem value="42">42 days</SelectItem>
                      <SelectItem value="28">28 days</SelectItem>
                      <SelectItem value="21">21 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Second Alert</Label>
                  <Select 
                    value={String(alertSettings.epc.second_alert)}
                    onValueChange={(v) => setAlertSettings({
                      ...alertSettings,
                      epc: { ...alertSettings.epc, second_alert: parseInt(v) }
                    })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="21">21 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="10">10 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Final Alert</Label>
                  <Select 
                    value={String(alertSettings.epc.final_alert)}
                    onValueChange={(v) => setAlertSettings({
                      ...alertSettings,
                      epc: { ...alertSettings.epc, final_alert: parseInt(v) }
                    })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="3">3 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Checkbox 
                  checked={alertSettings.epc.send_sms}
                  onCheckedChange={(checked) => setAlertSettings({
                    ...alertSettings,
                    epc: { ...alertSettings.epc, send_sms: checked }
                  })}
                />
                <Label className="text-sm">Send SMS alerts</Label>
              </div>
            </div>

            {/* EICR Settings */}
            <div className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold">EICR (Electrical Installation)</h3>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAlertSettings({
                    ...alertSettings,
                    eicr: { ...alertSettings.eicr, enabled: !alertSettings.eicr.enabled }
                  })}
                  className={alertSettings.eicr.enabled ? "text-green-600" : "text-slate-400"}
                >
                  {alertSettings.eicr.enabled ? <ToggleRight className="w-6 h-6" /> : <ToggleLeft className="w-6 h-6" />}
                  <span className="ml-1 text-xs">{alertSettings.eicr.enabled ? "Enabled" : "Disabled"}</span>
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs text-muted-foreground">First Alert</Label>
                  <Select 
                    value={String(alertSettings.eicr.first_alert)}
                    onValueChange={(v) => setAlertSettings({
                      ...alertSettings,
                      eicr: { ...alertSettings.eicr, first_alert: parseInt(v) }
                    })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="90">90 days</SelectItem>
                      <SelectItem value="56">56 days</SelectItem>
                      <SelectItem value="42">42 days</SelectItem>
                      <SelectItem value="28">28 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Second Alert</Label>
                  <Select 
                    value={String(alertSettings.eicr.second_alert)}
                    onValueChange={(v) => setAlertSettings({
                      ...alertSettings,
                      eicr: { ...alertSettings.eicr, second_alert: parseInt(v) }
                    })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="42">42 days</SelectItem>
                      <SelectItem value="28">28 days</SelectItem>
                      <SelectItem value="21">21 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Final Alert</Label>
                  <Select 
                    value={String(alertSettings.eicr.final_alert)}
                    onValueChange={(v) => setAlertSettings({
                      ...alertSettings,
                      eicr: { ...alertSettings.eicr, final_alert: parseInt(v) }
                    })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="21">21 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Checkbox 
                  checked={alertSettings.eicr.send_sms}
                  onCheckedChange={(checked) => setAlertSettings({
                    ...alertSettings,
                    eicr: { ...alertSettings.eicr, send_sms: checked }
                  })}
                />
                <Label className="text-sm">Send SMS alerts</Label>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <AlertCircle className="w-4 h-4 inline mr-1" />
                <strong>How it works:</strong> Alerts are sent at each threshold (first, second, final) if the landlord hasn't responded. 
                Each alert has increasing urgency, with the final alert marked as URGENT.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsModal(false)}>Cancel</Button>
            <Button onClick={saveAlertSettings} disabled={savingSettings}>
              {savingSettings ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Save Settings
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Renewal Modal */}
      <Dialog open={showBulkRenewalModal} onOpenChange={setShowBulkRenewalModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5" />
              Bulk Certificate Renewal
            </DialogTitle>
            <DialogDescription>
              Order certificate renewals for {selectedProperties.length} properties
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Building className="w-5 h-5 text-blue-600" />
                <span className="font-semibold">{selectedProperties.length} properties selected</span>
              </div>
              {selectedProperties.length >= 3 && (
                <Badge className="bg-green-100 text-green-800">5% Bulk Discount Applied!</Badge>
              )}
            </div>

            <div>
              <Label>Certificate Type</Label>
              <Select value={bulkRenewalType} onValueChange={setBulkRenewalType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gas_safety">
                    <div className="flex items-center gap-2">
                      <Flame className="w-4 h-4 text-orange-500" />
                      Gas Safety Certificate - £125 + VAT
                    </div>
                  </SelectItem>
                  <SelectItem value="epc">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-green-500" />
                      EPC - £125 + VAT
                    </div>
                  </SelectItem>
                  <SelectItem value="eicr">
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-blue-500" />
                      EICR - £250 + VAT
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Payment Method</Label>
              <Select value={bulkPaymentMethod} onValueChange={setBulkPaymentMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="invoice">Invoice Landlords</SelectItem>
                  <SelectItem value="deduct_from_rent">Deduct from Rent (where applicable)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="font-medium mb-2">Order Summary</h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>Properties:</span>
                  <span>{selectedProperties.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Unit Price (ex VAT):</span>
                  <span>£{bulkRenewalType === "eicr" ? "250.00" : "125.00"}</span>
                </div>
                {selectedProperties.length >= 3 && (
                  <div className="flex justify-between text-green-600">
                    <span>Bulk Discount (5%):</span>
                    <span>-£{((bulkRenewalType === "eicr" ? 250 : 125) * 0.05 * selectedProperties.length).toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold border-t pt-2 mt-2">
                  <span>Estimated Total (inc VAT):</span>
                  <span>
                    £{(
                      (bulkRenewalType === "eicr" ? 250 : 125) * 
                      (selectedProperties.length >= 3 ? 0.95 : 1) * 
                      1.2 * 
                      selectedProperties.length
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkRenewalModal(false)}>Cancel</Button>
            <Button onClick={handleBulkRenewal} disabled={processingBulkOrder}>
              {processingBulkOrder ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ShoppingCart className="w-4 h-4 mr-2" />}
              Create {selectedProperties.length} Orders
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Inspection Modal */}
      <Dialog open={showBulkInspectionModal} onOpenChange={setShowBulkInspectionModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Schedule Bulk Inspections
            </DialogTitle>
            <DialogDescription>
              Schedule property inspections for {selectedProperties.length} properties
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Building className="w-5 h-5 text-blue-600" />
                <span className="font-semibold">{selectedProperties.length} properties selected</span>
              </div>
            </div>

            <div>
              <Label>Inspection Type</Label>
              <Select 
                value={bulkInspectionData.inspection_type} 
                onValueChange={(v) => setBulkInspectionData({...bulkInspectionData, inspection_type: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="routine">Routine Inspection</SelectItem>
                  <SelectItem value="move_in">Move-In Inspection</SelectItem>
                  <SelectItem value="move_out">Move-Out Inspection</SelectItem>
                  <SelectItem value="periodic">Periodic Inspection</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Scheduled Date</Label>
              <Input 
                type="date"
                value={bulkInspectionData.scheduled_date}
                onChange={(e) => setBulkInspectionData({...bulkInspectionData, scheduled_date: e.target.value})}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Repeat Every</Label>
                <Select 
                  value={String(bulkInspectionData.frequency_months)} 
                  onValueChange={(v) => setBulkInspectionData({...bulkInspectionData, frequency_months: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 month</SelectItem>
                    <SelectItem value="3">3 months</SelectItem>
                    <SelectItem value="6">6 months</SelectItem>
                    <SelectItem value="12">12 months</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Alert Before</Label>
                <Select 
                  value={String(bulkInspectionData.alert_days_before)} 
                  onValueChange={(v) => setBulkInspectionData({...bulkInspectionData, alert_days_before: parseInt(v)})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="21">21 days</SelectItem>
                    <SelectItem value="28">28 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea 
                value={bulkInspectionData.notes}
                onChange={(e) => setBulkInspectionData({...bulkInspectionData, notes: e.target.value})}
                placeholder="Any notes for the inspections..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkInspectionModal(false)}>Cancel</Button>
            <Button onClick={handleBulkInspection} disabled={processingBulkInspection || !bulkInspectionData.scheduled_date}>
              {processingBulkInspection ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
              Schedule {selectedProperties.length} Inspections
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
